import { useEffect, useState } from "react";
import { base44 } from "@/api/base44Client";
import AdminGuard from "@/components/admin/AdminGuard";
import AdminLayout from "@/components/admin/AdminLayout";
import { useApp } from "@/lib/AppContext";

const T = {
  ar: {
    title: "إدارة التقييمات",
    all: "الكل", pending: "بانتظار الموافقة", approved: "معتمدة",
    approve: "اعتماد", reject: "رفض", featured: "مميز", unfeatured: "إلغاء التمييز",
    empty: "لا توجد تقييمات",
    copyLink: "📋 انسخ رابط التقييم العام",
    copied: "✅ تم النسخ!",
    delete: "حذف",
    stars: "نجوم",
  },
  en: {
    title: "Manage Reviews",
    all: "All", pending: "Pending Approval", approved: "Approved",
    approve: "Approve", reject: "Reject", featured: "Feature", unfeatured: "Unfeature",
    empty: "No reviews found",
    copyLink: "📋 Copy General Review Link",
    copied: "✅ Copied!",
    delete: "Delete",
    stars: "stars",
  }
};

function Stars({ count }) {
  return (
    <span className="text-amber-400 text-sm">{"★".repeat(count)}{"☆".repeat(5 - count)}</span>
  );
}

export default function AdminReviews() {
  const { lang } = useApp();
  const tr = T[lang] || T.ar;
  const isAr = lang === "ar";
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(null);

  const load = () => {
    setLoading(true);
    base44.entities.Review.list("-created_date", 100)
      .then(data => { setReviews(data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const filtered = reviews.filter(r => {
    if (tab === "pending") return !r.is_approved;
    if (tab === "approved") return r.is_approved;
    return true;
  });

  const update = async (id, data) => {
    setSaving(id);
    const updated = await base44.entities.Review.update(id, data);
    setReviews(prev => prev.map(r => r.id === id ? updated : r));
    setSaving(null);
  };

  const deleteReview = async (id) => {
    if (!window.confirm(isAr ? "هل أنت متأكد من الحذف؟" : "Are you sure you want to delete?")) return;
    await base44.entities.Review.delete(id);
    setReviews(prev => prev.filter(r => r.id !== id));
  };

  const copyLink = (orderId, orderNumber, customerName) => {
    const base = window.location.origin;
    const params = new URLSearchParams();
    if (orderId) params.set("orderId", orderId);
    if (orderNumber) params.set("orderNumber", orderNumber);
    if (customerName) params.set("name", customerName);
    const url = `${base}/rate?${params.toString()}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const copyGeneralLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/rate`).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const tabs = [
    { key: "all", label: tr.all, count: reviews.length },
    { key: "pending", label: tr.pending, count: reviews.filter(r => !r.is_approved).length },
    { key: "approved", label: tr.approved, count: reviews.filter(r => r.is_approved).length },
  ];

  return (
    <AdminGuard>
      <AdminLayout>
        <div className="space-y-5" dir={isAr ? "rtl" : "ltr"}>
          {/* Header */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h1 className="text-xl font-black text-foreground">{tr.title}</h1>
            <button onClick={copyGeneralLink}
              className="inline-flex items-center gap-2 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 font-bold px-4 py-2.5 rounded-xl text-sm border border-amber-200 dark:border-amber-800 hover:opacity-80 transition-all">
              {copied ? tr.copied : tr.copyLink}
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 flex-wrap">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                  tab === t.key ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/80"
                }`}>
                {t.label} <span className="opacity-70">({t.count})</span>
              </button>
            ))}
          </div>

          {/* Reviews List */}
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => <div key={i} className="bg-card rounded-2xl p-5 animate-pulse border border-border h-24" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">{tr.empty}</div>
          ) : (
            <div className="space-y-3">
              {filtered.map(r => (
                <div key={r.id} className={`bg-card border rounded-2xl p-4 transition-all ${r.is_approved ? "border-border" : "border-amber-400/50 dark:border-amber-600/40 bg-amber-50/30 dark:bg-amber-900/10"}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <p className="font-bold text-foreground text-sm">{r.customer_name}</p>
                        {r.is_featured && <span className="text-amber-400 text-xs">⭐ {isAr ? "مميز" : "Featured"}</span>}
                        {!r.is_approved && <span className="bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-[10px] font-bold px-2 py-0.5 rounded-full">{isAr ? "بانتظار الموافقة" : "Pending"}</span>}
                      </div>
                      <Stars count={r.rating} />
                      {r.order_number && <p className="text-xs text-muted-foreground mt-0.5">#{r.order_number}</p>}
                      {r.comment && <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{r.comment}</p>}
                    </div>
                    <div className="flex flex-wrap gap-2 flex-shrink-0">
                      {!r.is_approved ? (
                        <button disabled={saving === r.id} onClick={() => update(r.id, { is_approved: true })}
                          className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-bold px-3 py-1.5 rounded-xl hover:opacity-80 transition-all disabled:opacity-60">
                          {tr.approve}
                        </button>
                      ) : (
                        <button disabled={saving === r.id} onClick={() => update(r.id, { is_approved: false })}
                          className="bg-secondary text-muted-foreground text-xs font-bold px-3 py-1.5 rounded-xl hover:opacity-80 transition-all disabled:opacity-60">
                          {tr.reject}
                        </button>
                      )}
                      <button disabled={saving === r.id} onClick={() => update(r.id, { is_featured: !r.is_featured })}
                        className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-bold px-3 py-1.5 rounded-xl hover:opacity-80 transition-all disabled:opacity-60">
                        {r.is_featured ? tr.unfeatured : tr.featured}
                      </button>
                      <button onClick={() => copyLink(r.order_id, r.order_number, r.customer_name)}
                        className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-bold px-3 py-1.5 rounded-xl hover:opacity-80 transition-all">
                        🔗
                      </button>
                      <button onClick={() => deleteReview(r.id)}
                        className="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 text-xs font-bold px-3 py-1.5 rounded-xl hover:opacity-80 transition-all">
                        {tr.delete}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </AdminLayout>
    </AdminGuard>
  );
}